import React, { useRef, useState } from 'react';
import { useFormik } from 'formik';
import Reaptcha from 'reaptcha';
import { FormattedMessage, useIntl } from 'gatsby-plugin-intl';
import * as Yup from 'yup';
import styled from 'styled-components';

const StyledButtonSubmit = styled.button`
  background: #ff4f0f;
  background: linear-gradient(125deg, #ff4f0f, #ff1053);
  color: white !important;
  border: 0;

  &:hover,
  &:active {
    background: #ff4f0f;
    background: linear-gradient(125deg, #ff4f0f, #ff1053) !important;
    color: white !important;
  }

  &.btn--loading:after {
    background-color: #ffffff !important;
  }
`;

const StyledMessageAlert = styled.div`
  display: flex;
  align-items: center;
  ${({ hasError }) => hasError && 'border: 1px solid #ff1053'};
  ${({ hasSuccess }) => hasSuccess && 'border: 1px solid #36b30b'};
  border-radius: 4px;
  padding: 20px;

  i {
    font-size: 30px;
    margin-right: 20px;
    ${({ hasError }) => hasError && 'color: #ff1053'};
    ${({ hasSuccess }) => hasSuccess && 'color: #36b30b'};
  }
`;

const FormContact = ({ catalogUrl }) => {
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [hasSuccess, setHasSuccess] = useState(false);
  const rcRef = useRef(null);

  const verifyCallback = recaptchaToken => {
    setFieldValue('recaptcha', recaptchaToken);
  };

  const handleChangeForm = () => {
    if (hasSuccess || hasError) {
      setHasError(false);
      setHasSuccess(false);
    }
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(intl.formatMessage({ id: 'form.errors.required' })),
    telephone: Yup.number(),
    recaptcha: Yup.string().required('Recaptcha obligatorio'),
    email: Yup.string()
      .email(intl.formatMessage({ id: 'form.errors.email' }))
      .required(intl.formatMessage({ id: 'form.errors.required' })),
    message: Yup.string().required(intl.formatMessage({ id: 'form.errors.required' }))
  });

  const {
    values,
    handleSubmit,
    handleChange,
    handleBlur,
    errors,
    touched,
    resetForm,
    setFieldValue
  } = useFormik({
    initialValues: {
      name: '',
      message: '',
      telephone: '',
      email: '',
      recaptcha: ''
    },
    validationSchema,
    onSubmit: async function (result) {
      try {
        setHasError(false);
        setHasSuccess(false);

        const body = JSON.stringify({ ...result, catalogUrl });

        setIsLoading(true);
        const response = await fetch(`/.netlify/functions/send-form-contact`, {
          method: 'POST',
          body
        });

        if (response.ok) {
          setHasSuccess(true);
          resetForm();
          rcRef.current.reset();
        } else {
          rcRef.current.reset();
          setHasError(true);
        }
        setIsLoading(false);
      } catch (e) {
        rcRef.current.reset();
        setIsLoading(false);
        setHasError(true);
      }
    }
  });

  return (
    <form onSubmit={handleSubmit} onChange={handleChangeForm}>
      <div className="form-email row">
        <div className="col-md-12">
          <label htmlFor="name">
            <FormattedMessage id="label.name" />
          </label>
          <input
            type="text"
            name="name"
            id="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            className={errors.name && touched.name ? 'field-error' : ''}
          />
          {errors.name && touched.name && <div className="message-error">{errors.name}</div>}
        </div>

        <div className="col-md-12">
          <label htmlFor="email">
            <FormattedMessage id="label.email" />
          </label>
          <input
            type="email"
            name="email"
            id="email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            className={errors.email && touched.email ? 'field-error' : ''}
          />
          {errors.email && touched.email && <div className="message-error">{errors.email}</div>}
        </div>

        <div className="col-md-12">
          <label htmlFor="telephone">
            <FormattedMessage id="label.telephone" />
          </label>
          <input
            type="text"
            name="telephone"
            id="telephone"
            value={values.telephone}
            onChange={handleChange}
            onBlur={handleBlur}
            className={errors.telephone && touched.telephone ? 'field-error' : ''}
          />
          {errors.telephone && touched.telephone && (
            <div className="message-error">{errors.telephone}</div>
          )}
        </div>

        <div className="col-md-12">
          <label htmlFor="message">
            <FormattedMessage id="label.message" />
          </label>
          <textarea
            rows="4"
            id="message"
            name="message"
            value={values.message}
            onChange={handleChange}
            onBlur={handleBlur}
            className={errors.message && touched.message ? 'field-error' : ''}
          />
          {errors.message && touched.message && (
            <div className="message-error">{errors.message}</div>
          )}
        </div>

        <div className="col-md-12">
          <Reaptcha
            ref={rcRef}
            sitekey={process.env.GATSBY_RECAPTCHA_TOKEN}
            render="explicit"
            verifyCallback={verifyCallback}
            onVerify={verifyCallback}
          />

          <div name="recaptcha" component="div" />
          {errors.recaptcha && <div className="message-error">{errors.recaptcha}</div>}
        </div>

        <div className="col-md-12">
          <StyledButtonSubmit
            type="submit"
            className={`btn btn--primary type--uppercase ${isLoading && 'btn--loading'}`}
          >
            <FormattedMessage id="label.submit" />
          </StyledButtonSubmit>
        </div>

        {(hasError || hasSuccess) && (
          <div className="mt-3">
            <StyledMessageAlert hasError={hasError} hasSuccess={hasSuccess}>
              {hasSuccess && (
                <>
                  <i className="icon-Mail-Send" />
                  <p>Nos pondremos en contacto contigo lo antes posible.</p>
                </>
              )}
              {hasError && (
                <>
                  <i className="icon-Error-404Window" />
                  <p>
                    Parece que tenemos algún tipo de problema, vuelva a intentarlo más tarde.
                    Perdone las molestias.
                  </p>
                </>
              )}
            </StyledMessageAlert>
          </div>
        )}
      </div>
    </form>
  );
};

export default FormContact;
